// 鶏・剣舞・軍配者たちの技の音 v1 (2026-10-31・音の専用セッション・記録 = docs/research/2026-10-31-boss-sfx-v1.md)。
//   それまで3体の技の音は0 (共通の唸り・機械音・足音だけ)。配布版の既定 = 三体とも鳴る (10-31 作者 blade=ok・twins=ok・11-01 rooster=ok)。
//   ?bossSfx=off = 三体とも前の音 (?bossSfx=v1 も残す・既定と同じ)。
//   作者の決まり (10-30)「予告には基本音がない／魔法系とか飛び道具、掛け声などはある／担ぐ音はいらない」＝
//   v1 は体の予告を無音にし、声 (鶏の鬨)・鈴・号令・飛び道具の予告だけ鳴らす。当たりは技の重さに比例 (小技は小さく)。
//   素材 = scripts/render_boss_sfx_v1.py → public/sfx/bsfx_v1_<名前>.wav (三体とも起動時に読む)。
//   名前が bsfx_v1_ で始まる = ボスの技の音 (src/sound_priority.ts)。ただし予告 (_tell_) はプレイヤーの音で下げない。
import type { SfxId } from './types'
import { bladeSfxV2Active, bladeSfxV2Files, bladeSfxV2On, bladeSfxV2Parried, bladeSfxV2Tell } from './blade_sfx_v2'

export const BOSS_SFX_V1_NAMES = [
  'rooster_tell_crow_short', 'rooster_tell_crow_long', 'rooster_tell_crow_call', 'rooster_tell_crow_finale',
  'rooster_feathers', 'rooster_claw', 'rooster_claw_last', 'rooster_dive', 'rooster_exec', 'rooster_parried',
  'blade_tell_knives', 'blade_slash', 'blade_thrust', 'blade_spin', 'blade_slash_heavy', 'blade_hem', 'blade_knife',
  'blade_exec', 'blade_parried',
  'twins_tell_bell', 'twins_tell_order', 'twins_tell_slide', 'twins_tell_sennichite', 'twins_tell_device',
  'twins_fan_swing', 'twins_fan_lunge', 'twins_bell_strike', 'twins_bell_shot', 'twins_order_wave', 'twins_slide',
  'twins_sennichite_last', 'twins_march', 'twins_device_slam', 'twins_parried_fan', 'twins_parried_bell',
  'twins_reveal_suck', 'twins_reveal_land',
] as const
export type BossSfxV1Name = typeof BOSS_SFX_V1_NAMES[number]
export const BOSS_SFX_V1_IDS = BOSS_SFX_V1_NAMES.map(n => `bsfx_v1_${n}` as const)

const ROOSTER = 'inverted-jester'
const BLADE = 'blade-dancer'
const TWINS = 'hollow-actor'
const BOSSES: readonly string[] = [ROOSTER, BLADE, TWINS]

const SHIPPED: readonly string[] = BOSSES
let onBosses: readonly string[] = SHIPPED
/** このボスで v1 の技の音を鳴らすか。 */
export function bossSfxOn(bossId: string): boolean { return onBosses.includes(bossId) }
export function setBossSfx(value: string | null | undefined): 'off' | 'v1' | 'shipped' {
  const mode = value === 'v1' ? 'v1' : value === 'off' ? 'off' : 'shipped'
  onBosses = mode === 'v1' ? BOSSES : mode === 'off' ? [] : SHIPPED
  if (mode === 'v1' && typeof fetch !== 'undefined') void import('./audio').then(m => m.preloadBossSfxV1())
  return mode
}
const id = (n: BossSfxV1Name): SfxId => `bsfx_v1_${n}`

// 予告: [音・溜めに入ってから鳴らす ms]。載っていない技は無音 (体の予告)。鶏の鬨は口を開く拍に合わせる (rooster_vertical.ts の crow 秒)。
const TELL: Readonly<Record<string, readonly [BossSfxV1Name, number]>> = {
  tokiZurashiShort: ['rooster_tell_crow_short', 0], tokiZurashiLong: ['rooster_tell_crow_long', 0],
  roosterDawnCall: ['rooster_tell_crow_call', 400], roosterCrowDrop: ['rooster_tell_crow_call', 420],
  crowExecution: ['rooster_tell_crow_finale', 500],
  bladeKnifeThrow: ['blade_tell_knives', 0],
}
/** v1 がこのボスの予告を受け持つか (受け持つなら共通の唸り・機械音・鶏の仮の鳴き声を鳴らさない)。 */
export function bossSfxOwnsTell(bossId: string): boolean { return bossSfxOn(bossId) }
// 剣舞は v2 (src/blade_sfx_v2.ts・11-04 作者採用) へ回す。?bossSfx=off の時は v2 も鳴らさない。
const v2 = (bossId: string): boolean => bossSfxOn(bossId) && bladeSfxV2On(bossId)
/** 溜めの prevMs → nowMs の間に鳴らす予告 (溜めに入った tick は prevMs = -1)。 */
export function bossSfxTellDue(bossId: string, attackId: string, prevMs: number, nowMs: number): SfxId | null {
  if (!bossSfxOwnsTell(bossId)) return null
  // 剣舞の v2: 予告は暗器を抜く音だけ (v1 の音のまま・溜めの頭)。
  if (v2(bossId)) return prevMs < 0 && nowMs >= 0 ? bladeSfxV2Tell(attackId) : null
  const t = TELL[attackId]
  return t && prevMs < t[1] && nowMs >= t[1] ? id(t[0]) : null
}

/** 当たりに入った時 (連撃は1発ごと) の音。null = 鳴らさない。 */
export function bossSfxActive(bossId: string, attackId: string, hitIndex: number, hits: number): SfxId | null {
  if (v2(bossId)) return bladeSfxV2Active(attackId, hitIndex, hits)
  if (!bossSfxOn(bossId)) return null
  const last = hitIndex >= hits - 1
  if (bossId === ROOSTER) {
    if (attackId === 'crowExecution') return id('rooster_exec')
    if (attackId === 'roosterCrowDrop' || attackId === 'roosterDawnCall') return id('rooster_dive')
    if (attackId === 'roosterAerialRoam') return id(hitIndex === 0 ? 'rooster_dive' : 'rooster_claw')
    return id(last && hits > 1 ? 'rooster_claw_last' : 'rooster_claw')
  }
  if (bossId === BLADE) {
    switch (attackId) {
      case 'dancerExecution': return id('blade_exec')
      case 'hemSweep': return id('blade_hem')
      case 'bladeKnifeThrow': return id('blade_knife')
      case 'pirouette': return id('blade_thrust')
      case 'bladeDescent': case 'bladeCrimson': return id('blade_slash_heavy')
      case 'bladeSpinChain': return id(last ? 'blade_slash_heavy' : 'blade_spin')
      case 'bladeGrand': case 'crescendo': return id(last ? 'blade_slash_heavy' : 'blade_slash')
      default: return id('blade_slash')
    }
  }
  return null
}

/** ボスの技が弾かれた時の音 (主人公の弾きの音に重ねる・攻撃の id から)。 */
export function bossSfxParried(bossId: string, attackId: string): SfxId | null {
  if (v2(bossId)) return bladeSfxV2Parried()
  if (!bossSfxOn(bossId)) return null
  if (bossId === ROOSTER) return id('rooster_parried')
  if (bossId === BLADE) return id('blade_parried')
  if (bossId === TWINS) return id(attackId.startsWith('twins_i_') ? 'twins_parried_bell' : 'twins_parried_fan')
  return null
}

// 軍配者たち (src/twins_fight.ts): 技の入り・当たりの湧き・奥の装置。
const TWIN_TELL: Readonly<Record<string, BossSfxV1Name>> = {
  i_bell: 'twins_tell_bell', a_order: 'twins_tell_order', i_slide: 'twins_tell_slide', j_sennichite: 'twins_tell_sennichite',
}
/** 技の入りの音。null = 前の音 (体の技は足音のまま)。 */
export function twinsSfxTell(moveId: string): SfxId | null {
  const n = bossSfxOn(TWINS) ? TWIN_TELL[moveId] : undefined
  return n ? id(n) : null
}
/** 当たりが湧いた時の音。 */
export function twinsSfxHit(moveId: string, defense: 'parry' | 'dodge'): SfxId | null {
  if (!bossSfxOn(TWINS)) return null
  switch (moveId) {
    case 'a_lunge': return id('twins_fan_lunge')
    case 'i_strike': case 'i_triple': return id('twins_bell_strike')
    case 'i_bell': return id('twins_bell_shot')
    case 'a_order': return id('twins_order_wave')
    case 'i_slide': return id('twins_slide')
    case 'j_sennichite': return id(defense === 'dodge' ? 'twins_sennichite_last' : 'twins_fan_swing')
    default: return id('twins_fan_swing')
  }
}
/** 奥の装置: 動き出し (予告)・駒が出る・叩きつけ。null = 前の音。 */
export function twinsSfxDevice(ev: 'start' | 'march' | 'slam'): SfxId | null {
  if (!bossSfxOn(TWINS)) return null
  return id(ev === 'start' ? 'twins_tell_device' : ev === 'march' ? 'twins_march' : 'twins_device_slam')
}

// 3部屋目の現れ方 C「上から落ちる重さ」の音 (11-01・音の専用セッション・記録 docs/research/2026-11-01-sfx-remake-and-twins.md)。
//   suck = 光が一点へ吸い込まれる (山が swallow に来る)・land = 装置が落ちて床を割る (山が appear に来る)。
//   配布版の既定で鳴る。開発版 ?twinsRevealSfx=off で前の形 (吸い込みは無音・着地は仮の hit_boss_heavy)。
/** 音の山がファイルの頭から何 ms にあるか (scripts の SUCK_PEAK・LAND_PEAK と同じ)。 */
export const TWINS_REVEAL_SFX_PEAK_MS = { suck: 1750, land: 230 } as const
let twinsRevealSfxOn = true
export function setTwinsRevealSfx(value: string | null | undefined): void { twinsRevealSfxOn = value !== 'off' }
/** 現れ方の音。null = 前の形 (吸い込みは鳴らさず、着地は呼ぶ側が仮の音を鳴らす)。 */
export function twinsSfxReveal(ev: 'suck' | 'land'): SfxId | null {
  if (!bossSfxOn(TWINS) || !twinsRevealSfxOn) return null
  return id(ev === 'suck' ? 'twins_reveal_suck' : 'twins_reveal_land')
}

/** 予告の音か (プレイヤーの音で下げない)。 */
export function isBossSfxV1Tell(sfx: string): boolean { return sfx.startsWith('bsfx_v1_') && sfx.includes('_tell_') }

/** 先読みする素材 [キー, URL]。 */
export function bossSfxV1Files(): [string, string][] {
  // 吸い込みは 11-01 夕に作り直した (長く・SF 寄りをやめた)。?v=2 はブラウザに残った前の音を読み直させるため。
  return BOSS_SFX_V1_IDS.map(k => [k, `/sfx/${k}.wav?v=${k === 'bsfx_v1_twins_reveal_suck' ? 2 : 1}`])
}
/** 配布版で鳴らす三体の素材 (preloadSfxSamples が読む)。剣舞の v2 も含む。 */
export function bossSfxShippedFiles(): [string, string][] {
  return [...bossSfxV1Files(), ...bladeSfxV2Files()]
}
